import React, { useEffect, useState } from 'react';
import {
    AppBar,
    Box,
    Divider,
    Toolbar,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    CircularProgress,
    Select,
    MenuItem,
    ListItemText,
    ListItemIcon,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSettings } from '../../../contexts/SettingContexts';
import { AccountPopover } from './AccountPopover';
import { styles } from './styles';
import { Logo } from '../../../features/account/Logo';
import { parseUrl, generateClientUrl } from '../../../utils/urlParser';
import { setCompanyReferer } from '../../../utils/cookieUtils';
import { useLogoutMutation } from '../../../api/login';
import { useGetAllCompaniesMutation } from '../../../api/company';
import { logout } from '../../../store/slices/session';
import { useSnackbar } from 'notistack';

const CompanyDropDown = () => {
    const [openDialog, setOpenDialog] = useState();
    const currentUser = useSelector((state) => state.session.user);
    const [getAllCompanies, { data: companiesData }] = useGetAllCompaniesMutation();

    useEffect(() => {
        getAllCompanies();
    }, []);

    const handleChange = async () => {
        setCompanyReferer(openDialog.slug);
        // Get URL info to determine navigation
        const urlInfo = parseUrl();
        const { environment } = urlInfo;

        const targetUrl = generateClientUrl(environment, openDialog.slug, '/');
        window.location.href = targetUrl;
    };

    return (
        <>
            <Select
                value={currentUser.company || openDialog?.name}
                onChange={(e) => e.target.value?.name !== currentUser.company && setOpenDialog(e.target.value)}
                variant="standard"
                disableUnderline
                IconComponent={(props) => (
                    <span {...props} style={{ top: '5px', height: '24px' }}>
                        <KeyboardArrowDownIcon />
                    </span>
                )}
                sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                }}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 300,
                            minWidth: 150,
                        },
                    },
                }}
                renderValue={() => (
                    <Typography variant="buttonMedium" color="primary">
                        {currentUser.company || openDialog?.name}
                    </Typography>
                )}
            >
                {companiesData?.companies?.map((company) => (
                    <MenuItem key={company.id} value={company}>
                        <ListItemText
                            primary={
                                <Typography variant="body1" fontWeight={500}>
                                    {company.name}
                                </Typography>
                            }
                            secondary={
                                <Typography variant="body2" color="text.secondary">
                                    {company.role_name}
                                </Typography>
                            }
                        />
                        {currentUser.company === company.name && (
                            <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                                <CheckIcon fontSize="small" color="warning" />
                            </ListItemIcon>
                        )}
                    </MenuItem>
                ))}
            </Select>

            <Dialog open={openDialog ? true : false}>
                <DialogContent sx={{ minWidth: '400px', maxWidth: '500px' }}>
                    <Box sx={styles.tableMenuDialog}>
                        <DialogTitle textAlign={'center'}>Confirm</DialogTitle>
                        <DialogContentText>{`Do you want to switch to company ${openDialog?.name}?`}</DialogContentText>
                    </Box>
                    <DialogActions>
                        <Button size="medium" variant="text" onClick={() => setOpenDialog(false)}>
                            No
                        </Button>
                        <Button size="medium" variant="contained" onClick={handleChange}>
                            Yes
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </>
    );
};

const PageNavbar = () => {
    const { i18n, t } = useTranslation();
    const [openDialog, setOpenDialog] = useState(false);
    const { settings, saveSettings } = useSettings();
    const [darkMode, setDarkMode] = useState(settings.theme === 'dark');
    const [rtlDirection, setRtlDirection] = useState(settings.direction === 'rtl');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const currentUser = useSelector((state) => state.session.user);
    const [logoutApi] = useLogoutMutation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const handleLogout = async () => {
        setLoading(true);
        const response = await logoutApi().unwrap();
        if (response !== null) {
            setLoading(false);
            dispatch(logout());
            navigate('/login');
        }
    };

    const handleLanguageChange = (language) => {
        i18n.changeLanguage(language);
        saveSettings({
            ...settings,
            language,
        });
        enqueueSnackbar(t('Language changed'), { variant: 'success' });
    };

    const handleSwitchTheme = () => {
        saveSettings({
            ...settings,
            theme: settings.theme === 'light' ? 'dark' : 'light',
        });

        setDarkMode(settings.theme === 'light');
    };

    const handleSwitchDirection = () => {
        saveSettings({
            ...settings,
            direction: settings.direction === 'ltr' ? 'rtl' : 'ltr',
        });

        setRtlDirection(settings.direction === 'rtl');
    };

    return (
        <AppBar elevation={0} sx={styles.appBar}>
            <Toolbar disableGutters sx={styles.toolBar}>
                <Typography noWrap component="a" sx={styles.centerFlex} href="/dashboard">
                    <Logo emblemOnly variant="light" />
                </Typography>
                <Divider flexItem orientation="vertical" sx={styles.divider} />
                {currentUser.company && (
                    <>
                        <CompanyDropDown />
                        <Divider flexItem orientation="vertical" sx={styles.divider} />
                    </>
                )}
                <Box sx={{ position: 'absolute', right: '24px' }}>
                    <AccountPopover
                        darkMode={darkMode}
                        onLanguageChange={handleLanguageChange}
                        onSwitchDirection={handleSwitchDirection}
                        onSwitchTheme={handleSwitchTheme}
                        rtlDirection={rtlDirection}
                        onLogout={() => setOpenDialog(true)}
                    />
                </Box>
            </Toolbar>
            <Dialog open={openDialog}>
                <DialogContent sx={{ width: '400px' }}>
                    <Box sx={styles.tableMenuDialog}>
                        <DialogTitle>Confirm</DialogTitle>
                        <DialogContentText>Do you want to logout?</DialogContentText>
                    </Box>
                    <DialogActions>
                        <Button size="medium" variant="text" onClick={() => setOpenDialog(false)} disabled={loading}>
                            No
                        </Button>
                        <Button
                            size="medium"
                            variant="contained"
                            onClick={handleLogout}
                            startIcon={loading ? <CircularProgress color="inherit" size="1.2rem" /> : <></>}
                        >
                            Yes
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </AppBar>
    );
};

export default PageNavbar;
